/**
 * AnalysisResultPage component
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 8.1, 8.2 - Complete analysis result page
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  useTheme,
  useMediaQuery,
  Button,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import HistoryIcon from '@mui/icons-material/History';
import { historyApi, type SessionDetail } from '../api';
import { CustomRadarChart } from './CustomRadarChart';
import { Gb74PersonalReport } from './Gb74PersonalReport';
import { unwrapGb74Scores } from '../utils/gb74Report';

export const AnalysisResultPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [result, setResult] = useState<SessionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch analysis result using history API
  useEffect(() => {
    const fetchResult = async () => {
      if (!sessionId) {
        setError('Session ID is required');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        console.log('[AnalysisResultPage] Fetching session detail for:', sessionId);
        const data = await historyApi.getSessionDetail(sessionId);
        console.log('[AnalysisResultPage] Received data:', data);
        setResult(data);
      } catch (err) {
        console.error('Failed to fetch analysis result:', err);
        setError('分析結果の取得に失敗しました。');
      } finally {
        setIsLoading(false);
      }
    };

    fetchResult();
  }, [sessionId]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        data-testid="loading-indicator"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert data-testid="error-message" severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/dashboard')}>
          ダッシュボードに戻る
        </Button>
      </Container>
    );
  }

  // No result state
  if (!result) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">分析結果が見つかりませんでした。</Alert>
        <Button variant="outlined" onClick={() => navigate('/dashboard')} sx={{ mt: 2 }}>
          ダッシュボードに戻る
        </Button>
      </Container>
    );
  }

  // GB74形式の結果は専用の個人レポートを表示する
  const gb74Scores = result.analysisResult ? unwrapGb74Scores(result.analysisResult.scores) : null;
  if (gb74Scores) {
    return (
      <Box component="main" role="main" data-testid="analysis-result-page" sx={{ minHeight: '100vh', bgcolor: '#FAFAF8' }}>
        <Gb74PersonalReport
          scores={result.analysisResult!.scores}
          userName={result.userName}
          meta={[result.questionSetTitle, result.completedAt ? formatDate(result.completedAt) : null].filter(Boolean).join('｜')}
          onNavigateToOrg={() => navigate('/dashboard')}
        />
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ pb: 6 }}>
          <Button variant="outlined" startIcon={<HistoryIcon />} onClick={() => navigate('/analysis/my-results')}>
            履歴一覧
          </Button>
          <Button variant="contained" startIcon={<HomeIcon />} onClick={() => navigate('/dashboard')}>
            ダッシュボード
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      role="main"
      data-testid="analysis-result-page"
      sx={{
        minHeight: '100vh',
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Container maxWidth="md">
        {/* Page Title */}
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          component="h1"
          gutterBottom
          sx={{ textAlign: 'center', mb: 2, fontWeight: 'bold' }}
        >
          分析結果
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', mb: 4 }}
        >
          {result.questionSetTitle}
        </Typography>

        {/* Main Result Card */}
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
          {/* Pattern Name */}
          {result.analysisResult?.patternName && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Chip
                label={result.analysisResult.patternName}
                color="primary"
                sx={{ fontSize: '1.2rem', py: 2, px: 3 }}
              />
            </Box>
          )}

          {/* Pattern Description */}
          {result.analysisResult?.patternDescription && (
            <Typography
              variant="body1"
              sx={{ textAlign: 'center', mb: 3, px: 2, lineHeight: 1.8 }}
            >
              {result.analysisResult.patternDescription}
            </Typography>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Scores Radar Chart */}
          <Typography variant="h6" gutterBottom>
            思考傾向チャート
          </Typography>
          {result.analysisResult ? (
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'center',
                maxWidth: isMobile ? '100%' : '400px',
                mx: 'auto',
              }}
            >
              <CustomRadarChart
                scores={{ scores: result.analysisResult.scores }}
                width={isMobile ? 250 : 350}
                height={isMobile ? 250 : 350}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              スコアデータがありません
            </Typography>
          )}

          {/* Date */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 3, textAlign: 'center' }}
          >
            実施日時: {result.completedAt ? formatDate(result.completedAt) : '未完了'}
          </Typography>
        </Paper>

        {/* Answer Details */}
        {result.answers && result.answers.length > 0 && (
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              回答内容
            </Typography>
            <Stack spacing={1}>
              {result.answers.map((answer, index) => (
                <Box
                  key={answer.questionId}
                  sx={{
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Q{index + 1}. {answer.questionText}
                  </Typography>
                  <Typography variant="body2" color="primary.main">
                    A. {answer.answerChoiceText}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Navigation Buttons */}
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => navigate('/analysis/my-results')}
          >
            履歴一覧
          </Button>
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/dashboard')}
          >
            ダッシュボード
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};
